import { TSong } from '@/context/global/types/song';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Helper: Get storage key based on user ID
const getUserStorageKey = (userId?: string | number | null): string => {
  if (!userId) return 'karaoke-storage-guest';
  return `karaoke-storage-user-${userId}`;
};

// Custom storage adapter that uses user-scoped keys
let currentUserId: string | number | null = null;

const createUserScopedStorage = () => {
  return {
    getItem: (name: string) => {
      const key = getUserStorageKey(currentUserId);
      const value = localStorage.getItem(key);
      return value;
    },
    setItem: (name: string, value: string) => {
      const key = getUserStorageKey(currentUserId);
      localStorage.setItem(key, value);
    },
    removeItem: (name: string) => {
      const key = getUserStorageKey(currentUserId);
      localStorage.removeItem(key);
    },
  };
};

type TKaraokeState = {
  queue: TSong[];
  currentSong: TSong | null;

  // from song detail
  sessionId: string | null;
  setSessionId: (id: string | null) => void;

  // to used with added music modal
  selectedSong: TSong | null;
  setSelectedSong: (song: TSong | null) => void;

  selectedSongs: TSong[] | null;
  setSelectedSongs: (songs: TSong[] | null) => void;

  // Pending songs for post-login restoration
  pendingSong: TSong | null;
  setPendingSong: (song: TSong | null) => void;

  pendingSongs: TSong[] | null;
  setPendingSongs: (songs: TSong[] | null) => void;

  isModalNoPackageOpen: boolean;
  setIsModalNoPackageOpen: (value: boolean) => void;

  addToQueue: (song: TSong | TSong[]) => void;
  playAllSongs: (songs: TSong[]) => void;

  moveToFirstQueueByIndex: (index: number) => void;
  removeFromQueue: (id: number) => void;
  removeByIndex: (index: number) => void;
  setCurrentSong: (song: TSong | null) => void;
  clearQueue: () => void;

  // User queue switching
  switchUserQueue: (userId: string | number | null) => void;
  migrateUserQueue: (oldUserId: string | number, newUserId: string | number) => void;
};

export const useKaraokeStore = create<TKaraokeState>()(
  persist(
    (set) => ({
      queue: [],
      currentSong: null,

      sessionId: null,
      setSessionId: (id) => set({ sessionId: id }),

      // Actions
      addToQueue: (song) => {
        set((state) => {
          const songs = Array.isArray(song) ? song : [song];

          if (songs.length === 0) return state;

          // If no current song is playing, set the first song as current and add rest to queue
          if (!state.currentSong && songs.length > 0) {
            return {
              currentSong: songs[0],
              queue: [...state.queue, ...songs.slice(1)],
            };
          }

          // Otherwise, add all songs to queue
          return {
            queue: [...state.queue, ...songs],
          };
        });
      },

      selectedSong: null,
      setSelectedSong: (song) => set({ selectedSong: song }),

      selectedSongs: null,
      setSelectedSongs: (songs) => set({ selectedSongs: songs }),

      pendingSong: null,
      setPendingSong: (song) => set({ pendingSong: song }),

      pendingSongs: null,
      setPendingSongs: (songs) => set({ pendingSongs: songs }),

      playAllSongs: (songs) => {
        set((state) => {
          if (songs.length === 0) return state;

          // First song becomes currentSong, rest go to top of queue
          return {
            currentSong: songs[0],
            queue: [...songs.slice(1), ...state.queue],
          };
        });
      },

      isModalNoPackageOpen: false,
      setIsModalNoPackageOpen: (value) => set(() => ({ isModalNoPackageOpen: value })),

      moveToFirstQueueByIndex: (index) =>
        set((state) => {
          if (index < 0 || index >= state.queue.length) return state;
          const song = state.queue[index];
          const rest = state.queue.filter((_, i) => i !== index);
          return { queue: [song, ...rest] };
        }),

      removeFromQueue: (id) =>
        set((state) => ({
          queue: state.queue.filter((song) => song.id !== id),
        })),

      removeByIndex: (index) =>
        set((state) => ({
          queue: state.queue.filter((_, i) => i !== index),
        })),

      setCurrentSong: (song) => set({ currentSong: song }),

      clearQueue: () => set({ queue: [] }),

      // Switch user queue: save current state and load new user's queue
      switchUserQueue: (userId) => {
        // Update current user ID for storage adapter
        currentUserId = userId;

        // Load persisted data for the new user from localStorage
        const key = getUserStorageKey(userId);
        const storedData = localStorage.getItem(key);

        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const persistedState = parsed.state || {};

            // Rehydrate with new user's data
            set({
              queue: persistedState.queue || [],
              currentSong: persistedState.currentSong || null,
              pendingSong: persistedState.pendingSong || null,
              pendingSongs: persistedState.pendingSongs || null,
            });
          } catch (error) {
            console.error('Failed to load user queue:', error);
            // Reset to empty state if parsing fails
            set({
              queue: [],
              currentSong: null,
              pendingSong: null,
              pendingSongs: null,
            });
          }
        } else {
          // No stored data for this user, reset to empty state
          set({
            queue: [],
            currentSong: null,
            pendingSong: null,
            pendingSongs: null,
          });
        }
      },

      // Migrate user queue from old userId to new userId
      migrateUserQueue: (oldUserId, newUserId) => {
        const oldKey = getUserStorageKey(oldUserId);
        const newKey = getUserStorageKey(newUserId);

        // Get data from old key
        const oldData = localStorage.getItem(oldKey);

        if (oldData) {
          try {
            // Copy data to new key
            localStorage.setItem(newKey, oldData);

            // Remove old key to clean up
            localStorage.removeItem(oldKey);

            console.log(`Successfully migrated queue from ${oldKey} to ${newKey}`);
          } catch (error) {
            console.error('Failed to migrate user queue:', error);
          }
        }

        // Switch to the new user's queue
        currentUserId = newUserId;

        // Load the migrated data
        const key = getUserStorageKey(newUserId);
        const storedData = localStorage.getItem(key);

        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const persistedState = parsed.state || {};

            set({
              queue: persistedState.queue || [],
              currentSong: persistedState.currentSong || null,
              pendingSong: persistedState.pendingSong || null,
              pendingSongs: persistedState.pendingSongs || null,
            });
          } catch (error) {
            console.error('Failed to load migrated queue:', error);
            set({
              queue: [],
              currentSong: null,
              pendingSong: null,
              pendingSongs: null,
            });
          }
        }
      },
    }),
    {
      name: 'karaoke-storage', // localStorage key (not used directly, but required by persist)
      storage: createJSONStorage(() => createUserScopedStorage()),
      partialize: (state) => ({
        // Only persist these fields, exclude pendingToasts and isOnKaraokePage
        // sessionId is excluded — it's a runtime value refreshed on each song fetch
        queue: state.queue,
        currentSong: state.currentSong,
        pendingSong: state.pendingSong,
        pendingSongs: state.pendingSongs,
      }),
    },
  ),
);
